import type { CityConfig } from "../city-template/types";

export const dalyCityConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Daly City",
  state: "California",
  stateAbbr: "CA",
  slug: "daly-city",
  route: "/cities/daly-city",
  title: "Emergency Passport Daly City, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Daly City, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Mateo County and Peninsula travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Daly City, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Daly City and Peninsula travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Daly%20City%20California%20hillside%20residential%20neighborhood%20at%20golden%20hour%20with%20rows%20of%20colorful%20houses%20on%20steep%20green%20hills%2C%20San%20Francisco%20skyline%20visible%20in%20the%20distance%20across%20the%20bay%2C%20Pacific%20Ocean%20fog%20rolling%20in%2C%20warm%20sunset%20light%2C%20professional%20suburban%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20peaceful%20coastal%20community%20atmosphere&width=1800&height=700&seq=daly-city-hero-001&orientation=landscape",
  heroImageAlt: "Daly City hillside neighborhood at golden hour with San Francisco skyline in the distance",
  geoPosition: "37.6879;-122.4702",
  geoPlacename: "Daly City, California",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown San Francisco — approximately 15–25 minutes from Daly City via I-280 or BART",
  },
  nearbyCitiesZips: [
    { name: "Daly City", zips: "94014, 94015, 94017" },
    { name: "South San Francisco", zips: "94080, 94083" },
    { name: "San Bruno", zips: "94066, 94067" },
    { name: "Pacifica", zips: "94044, 94045" },
    { name: "Brisbane", zips: "94005" },
    { name: "Colma", zips: "94014" },
    { name: "Broadmoor", zips: "94014" },
    { name: "Millbrae", zips: "94030" },
    { name: "Burlingame", zips: "94010, 94011" },
    { name: "San Mateo", zips: "94401–94404" },
    { name: "Foster City", zips: "94404" },
    { name: "Half Moon Bay", zips: "94019" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Peninsula departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "Santa Clara", route: "/cities/santa-clara" },
    { name: "Sunnyvale", route: "/cities/sunnyvale" },
    { name: "Santa Rosa", route: "/cities/santa-rosa" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Daly City remotely via secure courier. We do not have a walk-in office in Daly City — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Peninsula travelers.",
  fpcOfficeAddress: "447 Sutter St, San Francisco, CA 94108",
  fpcOfficeDistance: "Serving all of San Mateo County",
  serviceAreaIntro: "From the hillside neighborhoods of Daly City to the coastal communities of Pacifica, from San Bruno to South San Francisco — Fast Passport Center serves the entire Peninsula. We regularly help travelers from Burlingame, Millbrae, and San Mateo get their passports fast.",
  nearbyCitiesIntro: "We serve all Daly City neighborhoods and nearby San Mateo County cities including South San Francisco, San Bruno, Pacifica, Millbrae, Burlingame, and San Mateo. If you are on the Peninsula, we have you covered.",
  howItWorksIntro: "Most Daly City travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO just minutes away, Daly City travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Daly City and Peninsula travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Daly City? Here are the most common ones we hear from Peninsula travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Peninsula international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Daniel K.",
      location: "Daly City, CA",
      rating: 5,
      text: "My flight to Seoul was in 2 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Linda M.",
      location: "South San Francisco, CA",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Hawaii. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Robert C.",
      location: "San Bruno, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Angela W.",
      location: "Pacifica, CA",
      rating: 5,
      text: "As a frequent international traveler from the Peninsula, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "dc-1",
      name: "Daly City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1100 Hillside Blvd",
      city: "Daly City",
      state: "CA",
      zip: "94014",
      phone: "(650) 755-2952",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Daly City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "dc-2",
      name: "South San Francisco Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "400 Grand Ave",
      city: "South San Francisco",
      state: "CA",
      zip: "94080",
      phone: "(650) 589-2501",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from South San Francisco BART",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "dc-3",
      name: "San Bruno Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "555 El Camino Real",
      city: "San Bruno",
      state: "CA",
      zip: "94066",
      phone: "(650) 589-2301",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from San Bruno BART",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for mid-Peninsula residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "dc-4",
      name: "Millbrae Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "501 Magnolia Ave",
      city: "Millbrae",
      state: "CA",
      zip: "94030",
      phone: "(650) 697-1551",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Millbrae BART/Caltrain",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern San Mateo County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "dc-5",
      name: "Burlingame Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "830 Burlingame Ave",
      city: "Burlingame",
      state: "CA",
      zip: "94010",
      phone: "(650) 342-3991",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Burlingame Caltrain",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for central Peninsula residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "dc-6",
      name: "San Mateo County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "555 County Center",
      city: "Redwood City",
      state: "CA",
      zip: "94063",
      phone: "(650) 363-4500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "12 miles from Daly City",
      website: "https://www.smcgov.org/clerk",
      websiteLabel: "San Mateo County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Peninsula photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Peninsula acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Peninsula applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Peninsula families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Peninsula travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Daly City." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Daly City address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Daly City before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Daly City for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Peninsula travelers." },
    { q: "How fast can I renew my passport in Daly City?", a: "Fast Passport Center offers fast passport renewal in Daly City in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my Asia trip?", a: "If your passport expires before your Asia trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Daly City in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near Daly City?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Daly City residents." },
    { q: "Can I expedite a child passport in Daly City?", a: "Yes. Fast Passport Center expedites child passports in Daly City for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Daly City with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Daly City?",
      a: "With Fast Passport Center's emergency courier service, Daly City travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Daly City?",
      a: "Popular options include the Daly City Post Office (94014), South San Francisco Post Office (94080), San Bruno Post Office (94066), and Millbrae Post Office (94030). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Daly City?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live on the Peninsula or near the coast?",
      a: "We serve all of San Mateo County including Pacifica, Half Moon Bay, Burlingame, San Mateo, and Foster City. Our overnight shipping labels work from any UPS location, so your location on the Peninsula doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Daly City?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Daly City?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Daly City, South San Francisco, and San Bruno offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Daly City?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Daly City?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Peninsula families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Daly City", address: "3 Serramonte Center, Daly City, CA 94015", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — South San Francisco", address: "225 Grand Ave, South San Francisco, CA 94080", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — San Bruno", address: "1150 El Camino Real, San Bruno, CA 94066", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.office.fedex.com/default/office" },
    { name: "UPS Store — Millbrae", address: "1350 El Camino Real, Millbrae, CA 94030", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walmart — Daly City", address: "1500 Sloat Blvd, San Francisco, CA 94132", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/cp/photo/1078904" },
    { name: "Rite Aid — Pacifica", address: "2000 Francisco Blvd, Pacifica, CA 94044", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/photo" },
    { name: "Postal Annex — Burlingame", address: "1212 Burlingame Ave, Burlingame, CA 94010", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/" },
    { name: "Costco — San Francisco", address: "450 10th St, San Francisco, CA 94103", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/photo" },
  ],
  dmvLocations: [
    {
      name: "DMV — Daly City",
      address: "1500 Sullivan Ave, Daly City, CA 94015",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Bruno",
      address: "420 E San Bruno Ave, San Bruno, CA 94066",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — South San Francisco",
      address: "429 E Grand Ave, South San Francisco, CA 94080",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Redwood City",
      address: "300 Brewster Ave, Redwood City, CA 94063",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Mateo County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "5 miles from Daly City",
      address: "San Francisco, CA 94128",
      note: "The closest major international airport for Daly City travelers. Direct flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "20 miles from Daly City",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Alternative international airport for East Bay and Peninsula travelers. Direct flights to Mexico and Europe.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "35 miles from Daly City",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for South Bay travelers. Domestic flights and some international connections.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { label: "California CDPH — Birth, Death & Marriage Records", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { label: "San Mateo County Clerk — Birth, Death & Marriage Records", url: "https://www.smcgov.org/clerk" },
    { label: "San Mateo Superior Court — Divorce Records", url: "https://www.sanmateocourt.org/" },
  ],
};